import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { waLink } from "../lib/site";
import { Link } from "react-router-dom";
import { getConsent, newEventId, visitorInfo } from "../lib/consent";
import { trackLead, trafficSource } from "../lib/tracking";
import { WhatsAppIcon } from "./Brand";

const CORE = ["name", "phone", "email", "company", "city", "message", "whatsapp", "state", "business_type", "product", "quantity"];

export const Field = ({ f, value, onChange, testPrefix }) => {
  const common = {
    id: `${testPrefix}-${f.name}`,
    name: f.name,
    required: f.required,
    value: value ?? "",
    onChange: (e) => onChange(f.name, e.target.value),
    className: "field",
    "data-testid": `${testPrefix}-${f.name}-input`,
    ...(f.attrs ?? {}),
  };
  return (
    <label className={`block ${f.full ? "sm:col-span-2" : ""}`} htmlFor={common.id}>
      <span className="mb-2 block text-sm font-semibold text-navy">
        {f.label} {f.required && <span className="text-brand">*</span>}
      </span>
      {f.type === "select" ? (
        <select {...common}>
          <option value="">Select…</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : f.type === "textarea" ? (
        <textarea {...common} rows={4} placeholder={f.placeholder} />
      ) : (
        <input {...common} type={f.type || "text"} placeholder={f.placeholder} />
      )}
    </label>
  );
};

export default function EnquiryForm({ type, fields, testPrefix, submitLabel = "Send enquiry", extraDetails = {}, waText, initial, successText }) {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const [agree, setAgree] = useState(false);

  useEffect(() => {
    if (initial) setValues((s) => ({ ...s, ...initial }));
  }, [initial]);

  const onChange = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const details = { ...extraDetails };
    const c = getConsent();
    const v = visitorInfo();
    const eventId = newEventId();
    const body = {
      type, details, consent: agree, visitor_id: v?.visitor_id ?? "", page: window.location.pathname,
      source: trafficSource().slice(0, 60), marketing_consent: !!c?.marketing, event_id: eventId,
      product: extraDetails.Product ?? "",
    };
    fields.forEach((f) => {
      const v = (values[f.name] ?? "").trim();
      if (CORE.includes(f.name)) { if (v || !body[f.name]) body[f.name] = v; }
      else if (v && f.name !== "quantity_unit") details[f.label] = v;
    });
    if (body.quantity && values.quantity_unit) body.quantity = `${body.quantity} ${values.quantity_unit}`.slice(0, 60);
    try {
      await api.post("/enquiries", body);
      trackLead(type, eventId);
      setDone(true);
      toast.success("Enquiry received. Our team will contact you shortly.");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        data-testid={`${testPrefix}-success`}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center"
      >
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
        <h3 className="mt-4 font-display text-3xl font-bold uppercase text-navy">Thank you!</h3>
        <p className="mt-2 text-slate-600">{successText ?? "Your enquiry has been received. We will get back to you soon."}</p>
        {values.email?.trim() && <p data-testid={`${testPrefix}-success-email`} className="mt-1 text-sm text-slate-500">A confirmation email has been sent to {values.email.trim()}.</p>}
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <a href={waLink(waText)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-white">
            <WhatsAppIcon /> Follow up on WhatsApp
          </a>
          <button data-testid={`${testPrefix}-new`} onClick={() => { setValues({}); setAgree(false); setDone(false); }} className="rounded-full border border-navy/20 px-5 py-3 text-sm font-semibold text-navy">
            Send another
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${testPrefix}-form`} className="grid gap-5 sm:grid-cols-2">
      {fields.map((f) => (
        <Field key={f.name} f={f} value={values[f.name]} onChange={onChange} testPrefix={testPrefix} />
      ))}
      <label className="sm:col-span-2 flex items-start gap-3 text-sm text-slate-600">
        <input type="checkbox" required checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid={`${testPrefix}-consent-checkbox`} className="mt-0.5 h-4 w-4 accent-[#FF6B00]" />
        <span>I agree that MHM SUPER ADBLUE may contact me by phone, WhatsApp or email about this enquiry. See our <Link to="/privacy-policy" className="font-semibold text-brand">privacy policy</Link>.</span>
      </label>
      <div className="sm:col-span-2 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={loading}
          data-testid={`${testPrefix}-submit-button`}
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 font-semibold text-white transition-[background-color,transform] hover:bg-brand-dark hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {submitLabel}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
        <a
          href={waLink(waText)}
          target="_blank"
          rel="noreferrer"
          data-testid={`${testPrefix}-whatsapp-button`}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/15 px-6 py-4 text-sm font-semibold text-navy transition-colors hover:border-[#25D366] hover:text-[#128C4B]"
        >
          <WhatsAppIcon /> Or enquire on WhatsApp
        </a>
      </div>
    </form>
  );
}
